import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  MessageSquare, Send, ArrowLeft, Clock, CheckCircle, XCircle,
  Package, User, MapPin, DollarSign, Calendar, Loader2, Circle
} from 'lucide-react';
import LiveChat, { ChatButton } from '../components/shared/LiveChat';

const BuyerInquiries = () => {
  const navigate = useNavigate();
  const { inquiryId } = useParams();
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);

  const fetchInquiries = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/inquiries?role=buyer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInquiryDetails = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInquiry(response.data.inquiry);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching inquiry details:', error);
      toast.error('Failed to load inquiry details');
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  useEffect(() => {
    if (inquiryId) {
      fetchInquiryDetails(inquiryId);
    }
  }, [inquiryId, fetchInquiryDetails]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedInquiry) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/inquiries/${selectedInquiry.inquiry_id}/message`, {
        inquiry_id: selectedInquiry.inquiry_id,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage('');
      fetchInquiryDetails(selectedInquiry.inquiry_id);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAcceptQuote = async () => {
    if (!selectedInquiry?.quoted_price) {
      toast.error('No price quoted yet');
      return;
    }

    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.post(`${API}/inquiries/${selectedInquiry.inquiry_id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quote accepted! Order created.');
      navigate(`/buyer/orders`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
      responded: { bg: 'bg-blue-100 text-blue-700 border-blue-200', icon: MessageSquare },
      negotiating: { bg: 'bg-purple-100 text-purple-700 border-purple-200', icon: MessageSquare },
      accepted: { bg: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      rejected: { bg: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      cancelled: { bg: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle }
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Detail View
  if (selectedInquiry) {
    const statusInfo = getStatusBadge(selectedInquiry.status);
    const StatusIcon = statusInfo.icon;

    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedInquiry(null);
              setMessages([]);
              navigate('/buyer/inquiries');
            }}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Inquiries
          </Button>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Inquiry Details */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Inquiry Details</CardTitle>
                <Badge className={`w-fit ${statusInfo.bg}`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {selectedInquiry.status}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Product</p>
                  <p className="font-medium">{selectedInquiry.product_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Seller</p>
                  <p className="font-medium">{selectedInquiry.seller_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium">{selectedInquiry.quantity_needed} units</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Target Price</p>
                    <p className="font-medium">
                      {selectedInquiry.target_price ? `$${selectedInquiry.target_price}` : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivery To</p>
                  <p className="font-medium">{selectedInquiry.delivery_country}</p>
                </div>
                {selectedInquiry.quoted_price && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Seller's Quote</p>
                    <p className="text-xl font-bold text-green-700">${selectedInquiry.quoted_price} per unit</p>
                    <p className="text-sm text-green-600">
                      Total: ${(selectedInquiry.quoted_price * selectedInquiry.quantity_needed).toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedInquiry.status === 'responded' && selectedInquiry.quoted_price && (
                  <Button 
                    onClick={handleAcceptQuote}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept Quote & Create Order
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Messages */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Conversation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] overflow-y-auto space-y-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  {/* Initial Inquiry */}
                  <div className="flex justify-end">
                    <div className="max-w-[80%] bg-blue-600 text-white p-3 rounded-lg rounded-br-sm">
                      <p className="text-sm">{selectedInquiry.message}</p>
                      <p className="text-xs text-blue-200 mt-1">
                        {new Date(selectedInquiry.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Seller Response */}
                  {selectedInquiry.seller_response && (
                    <div className="flex justify-start">
                      <div className="max-w-[80%] bg-white border p-3 rounded-lg rounded-bl-sm shadow-sm">
                        <p className="text-xs font-medium text-gray-500 mb-1">{selectedInquiry.seller_name}</p>
                        <p className="text-sm text-gray-700">{selectedInquiry.seller_response}</p>
                      </div>
                    </div>
                  )}

                  {/* Messages Thread */}
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_role === 'buyer' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.sender_role === 'buyer'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-white border shadow-sm rounded-bl-sm'
                      }`}>
                        {msg.sender_role !== 'buyer' && (
                          <p className="text-xs font-medium text-gray-500 mb-1">{msg.sender_name}</p>
                        )}
                        <p className={`text-sm ${msg.sender_role === 'buyer' ? 'text-white' : 'text-gray-700'}`}>
                          {msg.content}
                        </p>
                        <p className={`text-xs mt-1 ${msg.sender_role === 'buyer' ? 'text-blue-200' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message Input */}
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    disabled={selectedInquiry.status === 'accepted' || selectedInquiry.status === 'rejected'}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={() => setShowLiveChat(true)}
                    className="bg-green-600 hover:bg-green-700"
                    title="Open Live Chat"
                    data-testid="open-live-chat-btn"
                  >
                    <Circle className="w-3 h-3 mr-1 fill-green-300" />
                    Live Chat
                  </Button>
                </div>

                {/* Live Chat Widget */}
                <LiveChat
                  inquiryId={selectedInquiry.inquiry_id}
                  currentUserId={user?.user_id}
                  otherPartyName={selectedInquiry.seller_name}
                  otherPartyId={selectedInquiry.seller_id}
                  productName={selectedInquiry.product_name}
                  isOpen={showLiveChat}
                  onClose={() => setShowLiveChat(false)}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // List View
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-charcoal">My Inquiries</h1>
            <p className="text-gray-600">Track your product inquiries and negotiations</p>
          </div>
          <Button onClick={() => navigate('/marketplace')} className="bg-blue-600 hover:bg-blue-700">
            Browse Products
          </Button>
        </div>

        {inquiries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No inquiries yet</h3>
              <p className="text-gray-500 mb-4">
                Start by browsing the marketplace and sending inquiries to sellers
              </p>
              <Button onClick={() => navigate('/marketplace')} className="bg-blue-600 hover:bg-blue-700">
                Browse Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => {
              const statusInfo = getStatusBadge(inquiry.status);
              const StatusIcon = statusInfo.icon;

              return (
                <Card
                  key={inquiry.inquiry_id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedInquiry(inquiry);
                    navigate(`/buyer/inquiries/${inquiry.inquiry_id}`);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-charcoal">{inquiry.product_name}</h3>
                          <Badge className={statusInfo.bg}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {inquiry.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-1 text-gray-600">
                            <User className="w-4 h-4" />
                            {inquiry.seller_name}
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <Package className="w-4 h-4" />
                            {inquiry.quantity_needed} units
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            {inquiry.delivery_country}
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(inquiry.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {inquiry.quoted_price && (
                          <div className="mt-2 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="text-green-600 font-medium">
                              Quoted: ${inquiry.quoted_price}/unit
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <ChatButton
                          inquiryId={inquiry.inquiry_id}
                          currentUserId={user?.user_id}
                          otherPartyName={inquiry.seller_name}
                          otherPartyId={inquiry.seller_id}
                          productName={inquiry.product_name}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BuyerInquiries;
